import React from 'react';
import { mdiRomanNumeral1, mdiRomanNumeral2, mdiRomanNumeral3 } from '@mdi/js';
import { Tooltip } from '@mui/material';
import { TooltipTransitionComponent } from '../../base/tooltip/tooltip-transition';
import * as S from './claim-timeline.styles';
import { TimelineStatusTag } from './timeline-status-tag';

export type TimelineCobRoman = 'I' | 'II' | 'III';

export interface ITimelineCobMarkers {
    roman?: TimelineCobRoman;
    crossover: boolean;
}

const ROMAN_TITLE: Record<TimelineCobRoman, string> = {
    I: 'Primary',
    II: 'Secondary',
    III: 'Tertiary',
};

const ROMAN_ICON: Record<TimelineCobRoman, string> = {
    I: mdiRomanNumeral1,
    II: mdiRomanNumeral2,
    III: mdiRomanNumeral3,
};

export function getTimelineCobRomanTitle(roman: TimelineCobRoman): string {
    return ROMAN_TITLE[roman];
}

/** Parse payer layer and crossover from a payment or transmission status string. */
export function getTimelineCobMarkers(status?: string | null): ITimelineCobMarkers {
    const value = (status ?? '').toUpperCase();

    let roman: TimelineCobRoman | undefined;
    if (value.includes('PRIMARY')) {
        roman = 'I';
    } else if (value.includes('SECONDARY')) {
        roman = 'II';
    } else if (value.includes('TERTIARY')) {
        roman = 'III';
    }

    return {
        roman,
        crossover: value.includes('FORWARDED'),
    };
}

export interface ITransmissionCobSource {
    paymentIds?: string[];
    statuses?: Array<{ status?: string }>;
}

/**
 * Transmissions use SUBMITTED/ACKNOWLEDGED/FORWARDED, not PROCESSEDASPRIMARY.
 * Roman numerals come from prior-payment count (same contract as Provider):
 * none → I, one → II, two → III. Status still supplies Crossover on FORWARDED.
 */
export function romanFromPaymentIds(paymentIds?: string[]): TimelineCobRoman | undefined {
    if (!paymentIds || paymentIds.length === 0) {
        return 'I';
    }
    if (paymentIds.length === 1) {
        return 'II';
    }
    if (paymentIds.length === 2) {
        return 'III';
    }
    return undefined;
}

export function getTransmissionCobMarkers(transmission: ITransmissionCobSource): ITimelineCobMarkers {
    const fromStatus = getTimelineCobMarkers(transmission.statuses?.[0]?.status);
    return {
        roman: fromStatus.roman ?? romanFromPaymentIds(transmission.paymentIds),
        crossover: fromStatus.crossover,
    };
}

const RomanBadge: React.FC<{ roman: TimelineCobRoman }> = ({ roman }) => (
    <Tooltip
        placement="top"
        title={getTimelineCobRomanTitle(roman)}
        TransitionComponent={TooltipTransitionComponent}
        enterTouchDelay={0}>
        <S.FlexWrapper>
            <S.SecondaryIcon path={ROMAN_ICON[roman]} size={1} />
        </S.FlexWrapper>
    </Tooltip>
);

export const TimelineCobMarkers: React.FC<ITimelineCobMarkers> = ({ roman, crossover }) => {
    if (!roman && !crossover) {
        return null;
    }

    return (
        <S.CobMarkers>
            {roman && <RomanBadge roman={roman} />}
            {crossover && <TimelineStatusTag tone="accent" label="Crossover" dot={false} />}
        </S.CobMarkers>
    );
};
